import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/api/client";
import { toast } from "sonner";
import {
  User,
  Phone,
  MapPin,
  Receipt,
  CalendarClock,
  Clock,
  FileText,
  Pencil,
  ArrowRight,
  TrendingUp,
  Ban,
  Trash2,
  RotateCcw,
  ExternalLink,
  CalendarPlus,
  Navigation,
  Download,
  Link as LinkIcon,
} from "lucide-react";
import { openInMaps, openDirections, syncToGoogleCalendar, effectiveTotal } from "@/lib/jobActions";
import { API_BASE } from "@/api/client";

const fmt = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const statusStyles = {
  scheduled: { bg: "bg-primary/15", text: "text-primary", ring: "ring-primary/40" },
  completed: { bg: "bg-emerald-500/15", text: "text-emerald-400", ring: "ring-emerald-500/40" },
  cancelled: { bg: "bg-destructive/15", text: "text-destructive", ring: "ring-destructive/40" },
};

const Row = ({ icon: Icon, label, value, testId, onClick }) => {
  const clickable = Boolean(onClick && value);
  return (
    <div className="flex items-start gap-3 py-2.5" data-testid={testId}>
      <div className="mt-0.5 h-8 w-8 rounded-lg bg-primary/10 border border-primary/25 flex items-center justify-center text-primary flex-shrink-0">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
        {clickable ? (
          <button
            type="button"
            onClick={onClick}
            className="text-sm font-medium break-words text-left inline-flex items-start gap-1 group hover:text-primary transition-colors"
          >
            <span className="underline decoration-dotted underline-offset-4">{value}</span>
            <ExternalLink className="h-3 w-3 mt-0.5 opacity-60 group-hover:opacity-100 flex-shrink-0" />
          </button>
        ) : (
          <div className="text-sm font-medium break-words">{value || "—"}</div>
        )}
      </div>
    </div>
  );
};

export default function JobDetailDialog({ open, onOpenChange, job, onEdit, onChanged }) {
  const { t, lang } = useLang();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!job) return null;

  const isCancelled = job.status === "cancelled";
  const totalExpenses = (job.expenses || []).reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const effTotal = effectiveTotal(job);
  const profit = effTotal - totalExpenses;
  const balance = effTotal - (parseFloat(job.deposit) || 0);
  const st = statusStyles[job.status] || statusStyles.scheduled;

  const changeStatus = async (newStatus) => {
    setBusy(true);
    try {
      const payload = { ...job, status: newStatus };
      delete payload.id;
      delete payload.created_at;
      delete payload.created_by;
      await api.put(`/jobs/${job.id}`, payload);
      toast.success(
        newStatus === "cancelled"
          ? (lang === "es" ? "Trabajo cancelado" : "Job cancelled")
          : (lang === "es" ? "Trabajo restaurado" : "Job restored")
      );
      setConfirmCancel(false);
      onChanged?.();
      onOpenChange(false);
    } catch {
      toast.error(lang === "es" ? "Error al actualizar" : "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/jobs/${job.id}`);
      toast.success(lang === "es" ? "Trabajo eliminado" : "Job deleted");
      setConfirmDelete(false);
      onChanged?.();
      onOpenChange(false);
    } catch {
      toast.error(lang === "es" ? "Error al eliminar" : "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[92vh] overflow-y-auto bg-card border-border p-0"
        data-testid="job-detail-dialog"
      >
        {/* Header — dark gradient banner */}
        <div className="relative p-6 pb-5 border-b border-border overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-transparent" />
          <div className="relative pr-8">
            <DialogTitle className="sr-only">{job.client_name}</DialogTitle>
            <DialogDescription className="sr-only">Job details</DialogDescription>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span
                className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full ring-1 ${st.bg} ${st.text} ${st.ring}`}
                data-testid="job-detail-status"
              >
                <span className={`h-1.5 w-1.5 rounded-full ${job.status === "completed" ? "bg-emerald-400" : job.status === "cancelled" ? "bg-destructive" : "bg-primary"}`} />
                {job.status}
              </span>
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <CalendarClock className="h-3 w-3" /> {job.date}
              </span>
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" /> {job.start_time} – {job.end_time}
              </span>
            </div>
            <h2
              className={`text-2xl md:text-3xl font-bold tracking-tight truncate ${isCancelled ? "line-through opacity-60" : ""}`}
              style={{ fontFamily: "Outfit" }}
              data-testid="job-detail-client"
            >
              {job.client_name}
            </h2>
            <p className={`text-sm text-muted-foreground mt-1 truncate ${isCancelled ? "line-through opacity-60" : ""}`} data-testid="job-detail-service">
              {job.service_type || "—"}
            </p>
          </div>
        </div>

        {/* Money summary strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 border-b border-border">
          <div className="p-4 border-r border-border">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total</div>
            <div className="mt-1 text-lg font-bold" style={{ fontFamily: "Outfit" }} data-testid="job-detail-total">
              {fmt(effTotal)}
            </div>
          </div>
          <div className="p-4 border-r border-border">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Deposit</div>
            <div className="mt-1 text-lg font-bold text-emerald-400" style={{ fontFamily: "Outfit" }} data-testid="job-detail-deposit">
              {fmt(job.deposit)}
            </div>
          </div>
          <div className="p-4 md:border-r border-border">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Balance</div>
            <div className="mt-1 text-lg font-bold" style={{ fontFamily: "Outfit" }} data-testid="job-detail-balance">
              {fmt(balance)}
            </div>
          </div>
          <div className="p-4">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
              <TrendingUp className="h-3 w-3" /> Net Profit
            </div>
            <div className="mt-1 text-lg font-bold text-primary" style={{ fontFamily: "Outfit" }} data-testid="job-detail-profit">
              {fmt(profit)}
            </div>
          </div>
        </div>

        {/* Details grid */}
        <div className="p-6 grid md:grid-cols-2 gap-x-8 gap-y-1">
          <Row icon={User} label="Client" value={job.client_name} testId="job-detail-row-client" />
          <Row icon={Phone} label="Contact" value={job.contact} testId="job-detail-row-contact" />
          <Row icon={MapPin} label="Move from" value={job.address_from} testId="job-detail-row-from" onClick={() => openInMaps(job.address_from)} />
          <Row icon={MapPin} label="Move to" value={job.address_to} testId="job-detail-row-to" onClick={() => openInMaps(job.address_to)} />
        </div>

        {/* From → To visual */}
        {(job.address_from || job.address_to) && (
          <div className="mx-6 mb-4 rounded-xl border border-border bg-background/40 p-4">
            <div className="flex items-center gap-3 text-sm">
              <button
                type="button"
                onClick={() => openInMaps(job.address_from)}
                disabled={!job.address_from}
                className="flex items-center gap-2 min-w-0 flex-1 text-left hover:text-primary transition-colors disabled:opacity-60 disabled:cursor-default disabled:hover:text-inherit"
                data-testid="job-detail-from-map-btn"
              >
                <div className="h-2 w-2 rounded-full bg-primary flex-shrink-0" />
                <span className="truncate underline decoration-dotted underline-offset-4">{job.address_from || "—"}</span>
              </button>
              <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <button
                type="button"
                onClick={() => openInMaps(job.address_to)}
                disabled={!job.address_to}
                className="flex items-center gap-2 min-w-0 flex-1 text-left hover:text-primary transition-colors disabled:opacity-60 disabled:cursor-default disabled:hover:text-inherit"
                data-testid="job-detail-to-map-btn"
              >
                <div className="h-2 w-2 rounded-full bg-emerald-400 flex-shrink-0" />
                <span className="truncate underline decoration-dotted underline-offset-4">{job.address_to || "—"}</span>
              </button>
            </div>
            {job.address_from && job.address_to && (
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  onClick={() => openDirections(job.address_from, job.address_to)}
                  className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
                  data-testid="job-detail-directions-btn"
                >
                  <Navigation className="h-3 w-3" /> {lang === "es" ? "Ver ruta en Google Maps" : "Get directions"}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Expenses */}
        {(job.expenses || []).length > 0 && (
          <div className="mx-6 mb-4 rounded-xl border border-border">
            <div className="px-4 py-2 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Receipt className="h-4 w-4 text-primary" /> Expenses
              </div>
              <div className="text-sm font-bold text-destructive">{fmt(totalExpenses)}</div>
            </div>
            <div className="divide-y divide-border">
              {job.expenses.map((e, i) => (
                <div key={i} className="px-4 py-2 flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{e.label || `Expense #${i + 1}`}</span>
                  <span className="font-medium">{fmt(e.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contracted vs Actual */}
        {(job.hourly_rate > 0 || job.estimated_hours > 0 || job.actual_hourly_rate > 0 || job.actual_hours > 0) && (
          <div className="mx-6 mb-4 grid md:grid-cols-2 gap-3">
            <div className="rounded-xl border border-border p-4">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-2">Contracted</div>
              <div className="flex items-baseline justify-between text-sm">
                <span className="text-muted-foreground">Rate</span>
                <span className="font-semibold">{fmt(job.hourly_rate)}/hr</span>
              </div>
              <div className="flex items-baseline justify-between text-sm mt-1">
                <span className="text-muted-foreground">Hours</span>
                <span className="font-semibold">{Number(job.estimated_hours || 0).toFixed(2)}h</span>
              </div>
              <div className="mt-2 pt-2 border-t border-border flex items-baseline justify-between">
                <span className="text-xs uppercase tracking-widest text-primary font-bold">Estimated</span>
                <span className="text-lg font-bold text-primary" style={{ fontFamily: "Outfit" }}>{fmt((job.hourly_rate||0) * (job.estimated_hours||0))}</span>
              </div>
            </div>
            <div className="rounded-xl border border-border p-4">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-2">Actual worked</div>
              <div className="flex items-baseline justify-between text-sm">
                <span className="text-muted-foreground">Rate</span>
                <span className="font-semibold">{fmt(job.actual_hourly_rate)}/hr</span>
              </div>
              <div className="flex items-baseline justify-between text-sm mt-1">
                <span className="text-muted-foreground">Hours</span>
                <span className="font-semibold">{Number(job.actual_hours || 0).toFixed(2)}h</span>
              </div>
              <div className="mt-2 pt-2 border-t border-border flex items-baseline justify-between">
                <span className="text-xs uppercase tracking-widest text-primary font-bold">Labor subtotal</span>
                <span className="text-lg font-bold" style={{ fontFamily: "Outfit" }}>{fmt((job.actual_hourly_rate||0) * (job.actual_hours||0))}</span>
              </div>
            </div>
          </div>
        )}

        {/* Extra charges */}
        {(job.extra_charges || []).length > 0 && (
          <div className="mx-6 mb-4 rounded-xl border border-border">
            <div className="px-4 py-2 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Receipt className="h-4 w-4 text-primary" /> Extra charges
              </div>
              <div className="text-sm font-bold text-primary">{fmt((job.extra_charges||[]).reduce((s,e)=>s+(parseFloat(e.amount)||0),0))}</div>
            </div>
            <div className="divide-y divide-border">
              {job.extra_charges.map((e, i) => (
                <div key={i} className="px-4 py-2 flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{e.label || `Charge #${i + 1}`}</span>
                  <span className="font-medium">{fmt(e.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        {job.notes && (
          <div className="mx-6 mb-4 rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 text-sm font-semibold mb-2">
              <FileText className="h-4 w-4 text-primary" /> Notes
            </div>
            <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap" data-testid="job-detail-notes">
              {job.notes}
            </p>
          </div>
        )}

        {/* Footer actions */}
        <div className="sticky bottom-0 bg-card/95 backdrop-blur border-t border-border p-4 flex flex-wrap items-center gap-2 justify-end">
          <Button
            variant="outline"
            onClick={() => window.open(`${API_BASE}/booking/${job.id}/pdf`, "_blank", "noopener,noreferrer")}
            className="rounded-full text-primary border-primary/40 hover:bg-primary/10 hover:text-primary"
            data-testid="job-detail-pdf-btn"
          >
            <Download className="h-4 w-4 mr-1" /> {lang === "es" ? "Descargar PDF" : "Download PDF"}
          </Button>
          <Button
            variant="outline"
            onClick={async () => {
              const url = `${window.location.origin}/booking/${job.id}`;
              try {
                await navigator.clipboard.writeText(url);
                toast.success(lang === "es" ? "Link copiado" : "Link copied");
              } catch {
                window.prompt(lang === "es" ? "Copia este link:" : "Copy this link:", url);
              }
            }}
            className="rounded-full"
            data-testid="job-detail-share-btn"
          >
            <LinkIcon className="h-4 w-4 mr-1" /> {lang === "es" ? "Copiar link" : "Copy link"}
          </Button>
          <Button
            variant="outline"
            onClick={() => syncToGoogleCalendar(job)}
            className="rounded-full text-blue-400 border-blue-400/40 hover:bg-blue-400/10 hover:text-blue-400"
            data-testid="job-detail-gcal-btn"
          >
            <CalendarPlus className="h-4 w-4 mr-1" /> Google Calendar
          </Button>
          <Button
            variant="outline"
            onClick={() => setConfirmDelete(true)}
            disabled={busy}
            className="rounded-full text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
            data-testid="job-detail-delete-btn"
          >
            <Trash2 className="h-4 w-4 mr-1" /> {lang === "es" ? "Eliminar" : "Delete"}
          </Button>
          {isCancelled ? (
            <Button
              variant="outline"
              onClick={() => changeStatus("scheduled")}
              disabled={busy}
              className="rounded-full"
              data-testid="job-detail-restore-btn"
            >
              <RotateCcw className="h-4 w-4 mr-1" /> {lang === "es" ? "Restaurar" : "Restore"}
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={() => setConfirmCancel(true)}
              disabled={busy}
              className="rounded-full"
              data-testid="job-detail-cancel-btn"
            >
              <Ban className="h-4 w-4 mr-1" /> {lang === "es" ? "Cancelar trabajo" : "Cancel job"}
            </Button>
          )}
          <Button
            onClick={() => { onOpenChange(false); onEdit?.(job); }}
            disabled={busy}
            className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
            data-testid="job-detail-edit-btn"
          >
            <Pencil className="h-4 w-4 mr-1" /> {lang === "es" ? "Editar" : "Edit"}
          </Button>
        </div>

        {/* Confirm cancel */}
        <AlertDialog open={confirmCancel} onOpenChange={setConfirmCancel}>
          <AlertDialogContent className="bg-card border-border" data-testid="job-cancel-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{lang === "es" ? "¿Cancelar este trabajo?" : "Cancel this job?"}</AlertDialogTitle>
              <AlertDialogDescription>
                {lang === "es"
                  ? "El trabajo se marcará como cancelado y aparecerá tachado. Podrás restaurarlo después."
                  : "The job will be marked as cancelled and shown with a strikethrough. You can restore it later."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-full">{lang === "es" ? "No" : "No"}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => changeStatus("cancelled")}
                className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground"
                data-testid="job-cancel-confirm-btn"
              >
                {lang === "es" ? "Sí, cancelar" : "Yes, cancel"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Confirm delete */}
        <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
          <AlertDialogContent className="bg-card border-border" data-testid="job-delete-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{lang === "es" ? "¿Eliminar este trabajo?" : "Delete this job?"}</AlertDialogTitle>
              <AlertDialogDescription>
                {lang === "es"
                  ? "Esta acción no se puede deshacer. El trabajo se borrará permanentemente del calendario y de los reportes."
                  : "This action cannot be undone. The job will be permanently removed from the calendar and reports."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-full">{lang === "es" ? "Cancelar" : "Cancel"}</AlertDialogCancel>
              <AlertDialogAction
                onClick={remove}
                className="rounded-full bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                data-testid="job-delete-confirm-btn"
              >
                {lang === "es" ? "Eliminar" : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
